import { FabricWorld } from '@/commons/fabricjs/FabricWorld';
import {
    initZoomCallback,
    initClickCallback,
    initPanCallback,
    onUpdateObjectCallback,
    selectCurrentLayerCallback,
    shouldZoomInOut,
    shouldZoomCloseToLimits,
} from '@/commons/fabricjs/FabricCallbackHelper';
import { applyTextDefaultConfig } from '@/commons/fabricjs/FabricTextHelper';
import { ImageLoaderManager } from '@/commons/assetsManager/ImageLoaderManager';
import { debounce } from '@/utils/debounce';
import {
    FINAL_IMAGE_WIDTH,
    FINAL_IMAGE_HEIGHT,
    FONT_SIZE_MULTIPLIER,
    MARGIN_TOP,
    MARGIN_BOTTOM,
    STICKER_POSITION_BOTTOM,
    GUIDETTI_STICKER_WIDTH,
    GUIDETTI_STICKER_HEIGHT,
    MIN_ZOOM,
    MAX_ZOOM,
} from '@/guidetti/projectConstantes';
import { FabricText, FabricImage, Point, Rect, Group, FabricObject } from 'fabric';
import type { IFabricWorldInitParams } from '@/commons/fabricjs/IFabricWorldInitParams';
import type { IImageField } from '@/typescript/interfaces/IImageField';
import type { ITextField } from '@/typescript/interfaces/ITextField';

class GFabricWorld extends FabricWorld {
    // #region SETUP
    zoomFactorMax: number = 3; // max zoom factor
    zoomFactorMin: number = 0.1;
    updateObjectCallback = debounce(onUpdateObjectCallback, 150);
    selectCurrentLayerCallback = selectCurrentLayerCallback(this);
    smoothImages: boolean = true;
    uniqueId: string = '';
    objectSelectedBelow: FabricObject | null = null;
    constructor() {
        super();
        // All those margin could be piloted by the front if needed
        this.imageMargin = {
            top: 295, // 25mm en 300dpi
            bottom: 531, // 45mm en 300dpi
        };
    }

    init(params: IFabricWorldInitParams) {
        super.init(params);
        this.uniqueId = params.uniqueId;
        this.smoothImages = params.smoothImages ?? true;
        if (this.fabricCanvas) {
            this.fabricCanvas.backgroundColor = 'white';
        }
        this.fabricCanvas?.renderTop();
        this.initListeners();
        this.displayImageFullCanvas();
    }

    // #endregion
    // #region FIELDS Methods
    addMultipleFields(fields: (ITextField | IImageField)[], addToCanvas = true) {
        const promiseArray: Promise<FabricText | FabricImage | undefined>[] = [];
        fields.forEach((field) => {
            if ((field as ITextField)?.text || (field as ITextField)?.defaultText) {
                const newText = this.addText(
                    (field as ITextField).text ?? (field as ITextField).defaultText,
                    field as ITextField,
                    false,
                    addToCanvas,
                );
                if (newText) {
                    promiseArray.push(newText);
                }
            } else {
                const newImage = this.addImage((field as IImageField).url ?? '', field, false, addToCanvas);
                if (newImage) {
                    promiseArray.push(newImage);
                }
            }
        });
        return Promise.all(promiseArray);
    }

    addText(text: string, textField?: ITextField, selectable = true, addToCanvas = true): Promise<FabricText | undefined> {
        if (!textField) return Promise.resolve(undefined);
        const {
            id,
            color,
            text: existingText,
            fontSize,
            isBold,
            isUnderlined,
            isItalic,
            transform: {
                position: { x: left, y: top },
                angle,
                scale: { x: scaleX, y: scaleY },
            },
        } = textField;
        const fText = new FabricText(existingText ?? text, {
            name: 'textName',
            id,
            frameSize: { width: this.canvasWidth, height: this.canvasHeight },
            fill: color,
            fontSize: fontSize * FONT_SIZE_MULTIPLIER,
            fontWeight: isBold ? 'bold' : 'normal',
            fontStyle: isItalic ? 'italic' : 'normal',
            left,
            top,
            angle,
            scaleX,
            scaleY,
            underline: isUnderlined,
            centeredRotation: true,
            selectable,
        });
        if (left + top === 0) {
            applyTextDefaultConfig(fText);
            // center inside canvas render
            const widthCenter = FINAL_IMAGE_WIDTH / 2 - fText.width / 2;
            const heightCenter = FINAL_IMAGE_HEIGHT / 2 - fText.height / 2;
            const center = new Point(widthCenter, heightCenter);
            fText.set('left', center.x);
            fText.set('top', center.y);
            fText.setCoords();
            fText.set('dirty', true);
        }
        if (addToCanvas) {
            this.fabricCanvas?.add(fText);
        }
        if (selectable) {
            this.fabricCanvas?.setActiveObject(fText);
        }
        return Promise.resolve(fText);
    }

    addImage(imageUrl: string, field: IImageField, selectable = true, addToCanvas = true) {
        let fImage: FabricImage;
        if (!imageUrl) {
            return Promise.resolve(undefined);
        }
        const loadImage = ImageLoaderManager.loadImage(imageUrl);
        return loadImage
            .then((img) => {
                if (img instanceof Error) {
                    console.error(img);
                    return;
                }
                const {
                    id,
                    transform: {
                        position: { x: left, y: top },
                        angle,
                        scale: { x: scaleX, y: scaleY },
                    },
                } = field;
                fImage = new FabricImage(img, {
                    id,
                    left,
                    top,
                    angle,
                    scaleX,
                    scaleY,
                    selectable,
                    imageSmoothing: this.smoothImages,
                    frameSize: { width: this.canvasWidth, height: this.canvasHeight },
                });
                this.fabricCanvas?.add(fImage);
                if (addToCanvas) {
                    this.fabricCanvas?.sendObjectBackwards(fImage);
                }
                if (selectable) {
                    this.fabricCanvas?.setActiveObject(fImage);
                }
            })
            .then(() => {
                return fImage;
            });
    }

    // #endregion
    // #region STICKER Methods
    addBackgroundColor(backgroundColor: string, scaleFactor = 1) {
        if (backgroundColor) {
            const rect = new Rect({
                width: FINAL_IMAGE_WIDTH * scaleFactor,
                height: FINAL_IMAGE_HEIGHT * scaleFactor,
                fill: backgroundColor,
                left: 0,
                top: 0,
                selectable: false,
            });
            this.fabricCanvas?.add(rect);
            this.fabricCanvas?.sendObjectToBack(rect);
        }
        this.fabricCanvas?.renderAll();
    }

    stickerAndBrand(scaleFactor = 1) {
        const STICKER_TARGET_SIZE = 496;
        const stickerGroup = new Group();
        stickerGroup.selectable = false;
        this.fabricCanvas?.bringObjectToFront(stickerGroup);

        return ImageLoaderManager.loadImage('./images/stickerFabrication.png').then((img) => {
            if (img instanceof Error) {
                console.error(img);
                return;
            }
            const scalingRatio = (STICKER_TARGET_SIZE * scaleFactor) / GUIDETTI_STICKER_WIDTH; /*  / STICKER_TARGET_SIZE */
            const left = (FINAL_IMAGE_WIDTH * scaleFactor) / 2 - (img.width / 2) * scalingRatio;
            const imageTopPositionFromMiddle = FINAL_IMAGE_HEIGHT - STICKER_POSITION_BOTTOM;
            const top = imageTopPositionFromMiddle - GUIDETTI_STICKER_HEIGHT * scalingRatio;
            const fSticker = new FabricImage(img, {
                left,
                top,
                scaleX: scalingRatio,
                scaleY: scalingRatio,
                selectable: false,
                imageSmoothing: this.smoothImages,
            });
            stickerGroup.add(fSticker);
            const fontSizeFromPt = 7.08 * 1.333 * 4 * scaleFactor;
            const idPositionXAdjust = left + GUIDETTI_STICKER_WIDTH * scalingRatio;
            const idPositionYAdjust = top + GUIDETTI_STICKER_HEIGHT * scalingRatio * 0.5;

            const uniqueIdfText = new FabricText(this.uniqueId, {
                fill: 'white',
                fontFamily: 'Bebas Neue',
                fontSize: fontSizeFromPt * 0.9,
                left: idPositionXAdjust,
                top: idPositionYAdjust,
                selectable: false,
            });
            uniqueIdfText.left -= 110 * scaleFactor;
            uniqueIdfText.top -= 21.33 * scaleFactor;
            stickerGroup.add(uniqueIdfText);
            stickerGroup.bringObjectToFront(uniqueIdfText);
            stickerGroup.sendObjectToBack(fSticker);
            this.fabricCanvas?.renderAll();
        });
    }

    // #endregion
    // #region GUIDES Methods
    addGuides() {
        const guidesGroup = super.addGuides(false, true);
        ImageLoaderManager.loadImage('./images/stripes.png').then((img) => {
            if (img instanceof Error) {
                console.error(img);
                return;
            }
            const marginTop = new FabricImage(img, {
                width: FINAL_IMAGE_WIDTH,
                height: MARGIN_TOP,
                left: 0,
                top: 0,
                opacity: 0.25,
                selectable: false,
                imageSmoothing: true,
            });
            const marginbottom = new FabricImage(img, {
                width: FINAL_IMAGE_WIDTH,
                height: MARGIN_BOTTOM,
                left: 0,
                top: FINAL_IMAGE_HEIGHT - MARGIN_BOTTOM,
                opacity: 0.25,
                selectable: false,
                imageSmoothing: true,
            });
            guidesGroup.add(marginTop, marginbottom);
            this.fabricCanvas?.renderAll();
        });
        const contour = new Rect({
            width: FINAL_IMAGE_WIDTH,
            height: FINAL_IMAGE_HEIGHT,
            fill: 'transparent',
            stroke: 'black',
            strokeDashArray: [25, 30],
            strokeWidth: 2,
            left: 0,
            top: 0,
        });
        guidesGroup.add(contour);
        this.fabricCanvas?.bringObjectToFront(guidesGroup);
        return guidesGroup;
    }

    addRodGuide() {
        const rodMmInitial = new Rect({
            height: 2,
            width: FINAL_IMAGE_WIDTH * 0.8,
            fill: 'black',
            originX: 'center',
            originY: 'center',
            left: FINAL_IMAGE_WIDTH / 2,
        });
        const rodCmInitial = rodMmInitial.clone();
        rodCmInitial.then((obj) => {
            obj.set('height', 4);
        });
        const textMeasure = new FabricText('mm', {
            fontSize: 20,
            fill: 'black',
            fontFamily: 'Bebas Neue',
            originX: 'center',
            originY: 'center',
            left: rodMmInitial.left - rodMmInitial.width / 2 - 25,
        });
        return Promise.all([rodMmInitial, rodCmInitial]).then((objs) => {
            const [rodMm, rodCm] = objs;
            for (let i = 0; i < FINAL_IMAGE_HEIGHT; i += 12) {
                if (i % 120 === 0) {
                    rodCm.clone().then((newRodCm) => {
                        newRodCm.set('top', i);
                        this.fabricCanvas?.add(newRodCm);
                        this.fabricCanvas?.bringObjectToFront(newRodCm);
                    });
                    textMeasure.clone().then((newTextMeasure) => {
                        newTextMeasure.set('top', i);
                        newTextMeasure.set('text', `${i / 12} mm`);
                        this.fabricCanvas?.add(newTextMeasure);
                        this.fabricCanvas?.bringObjectToFront(newTextMeasure);
                    });
                } else {
                    rodMm.clone().then((newRodMm) => {
                        newRodMm.set('top', i);
                        this.fabricCanvas?.add(newRodMm);
                        this.fabricCanvas?.bringObjectToFront(newRodMm);
                    });
                }
            }
        });
    }

    // #endregion
    // #region HELPERS Methods
    displayImageFullCanvas() {
        const { top, bottom } = this.imageMargin;
        const imgScaleFactor = this.canvasHeight / (FINAL_IMAGE_HEIGHT + (top ?? 0) + (bottom ?? 0));
        this.zoomFactorMin = imgScaleFactor;

        this.fabricCanvas?.setZoom(imgScaleFactor);
        // eslint-disable-next-line  @typescript-eslint/no-unused-vars
        const [zoom0, number1, number2, zoom3] = this.fabricCanvas!.viewportTransform;
        this.fabricCanvas?.setViewportTransform([
            zoom0,
            number1,
            number2,
            zoom3,
            this.canvasWidth / 2 - (FINAL_IMAGE_WIDTH / 2) * this.fabricCanvas.getZoom(),
            this.canvasHeight / 2 - (FINAL_IMAGE_HEIGHT / 2) * this.fabricCanvas.getZoom(),
        ]);
    }

    focusOnElement(elementId: string) {
        const element = this.getFabricElementById(elementId);
        if (element) {
            const { x, y } = element.getCenterPoint();
            const bBox = element.getBoundingRect();
            const calcScalefactor = (bBox.width > bBox.height ? this.canvasWidth / bBox.width : this.canvasHeight / bBox.height) * 0.5;
            const scaleFactor = Math.min(calcScalefactor, 0.7);
            this.fabricCanvas?.setViewportTransform([
                scaleFactor,
                this.fabricCanvas!.viewportTransform[1],
                this.fabricCanvas!.viewportTransform[2],
                scaleFactor,
                this.canvasWidth / 2 - x * scaleFactor,
                this.canvasHeight / 2 - y * scaleFactor,
            ]);
            this.fabricCanvas?.renderAll();
        }
    }

    displayRealSizeCanvas() {
        this.fabricCanvas?.setZoom(1);
        const [zoom0, number1, number2, zoom3] = this.fabricCanvas!.viewportTransform;
        this.fabricCanvas?.setViewportTransform([zoom0, number1, number2, zoom3, 0, 0]);
    }

    getFabricElementById(id: string): FabricImage | FabricText | undefined {
        return this.fabricCanvas?.getObjects().find((obj) => {
            if (obj instanceof FabricImage && (obj as FabricImage & { id: string }).id === id) {
                return obj;
            }
            if (obj instanceof FabricText && (obj as FabricText & { id: string }).id === id) {
                return obj;
            }
            return null;
        }) as FabricImage | FabricText | undefined;
    }

    // 0.1 is the min zoom, 2.5 is the max zoom
    zoomInOut(
        zoomValue: {
            zoomRef: { value: number };
            oldZoom: number;
        },
        zoomLimit: { minZoom: number; maxZoom: number } = { minZoom: MIN_ZOOM, maxZoom: MAX_ZOOM }
    ) {
        const zoomCenter = this.fabricCanvas?.getCenterPoint();
        if (!zoomCenter) {
            return null;
        }
        const shouldZoom = shouldZoomInOut(zoomValue.zoomRef.value, zoomLimit.minZoom, zoomLimit.maxZoom);
        const getCloseToLimits = shouldZoomCloseToLimits(
            { actualValue: zoomValue.zoomRef.value, oldZoom: zoomValue.oldZoom },
            zoomLimit.minZoom,
            zoomLimit.maxZoom,
        );
        if (shouldZoom || getCloseToLimits) {
            this.fabricCanvas?.zoomToPoint(zoomCenter, zoomValue.zoomRef.value);
        } else {
            zoomValue.zoomRef.value = zoomValue.oldZoom;
        }
        this.fabricCanvas?.renderAll();
        return null;
    }

    initListeners() {
        const zoomCallback = initZoomCallback(this);
        if (zoomCallback instanceof Error) {
            throw zoomCallback;
        } else {
            this.zoomCallback = zoomCallback;
            this.fabricCanvas?.on('mouse:wheel', this.zoomCallback);
        }
        const clickCallback = initClickCallback(this);
        if (clickCallback instanceof Error) {
            throw clickCallback;
        } else {
            this.clickCallback = clickCallback;
            this.fabricCanvas?.on('mouse:down', this.clickCallback);
            this.fabricCanvas?.on('mouse:up', this.clickCallback);
        }

        const panCallback = initPanCallback(this);
        if (panCallback instanceof Error) {
            throw panCallback;
        } else {
            this.panCallback = panCallback;
            this.fabricCanvas?.on('mouse:move', this.panCallback);
        }
        // Update canvas Element
        this.fabricCanvas?.on('object:modified', this.updateObjectCallback);
        this.fabricCanvas?.on('mouse:down:before', this.selectCurrentLayerCallback);
        this.fabricCanvas?.on('object:added', this.updateObjectCallback);
    }

    dispose() {
        this.fabricCanvas?.off('object:modified', this.updateObjectCallback);
        this.fabricCanvas?.off('mouse:down:before', this.selectCurrentLayerCallback);
        this.fabricCanvas?.off('object:added', this.updateObjectCallback);
        this.fabricCanvas?.off('mouse:move', this.panCallback);
        this.fabricCanvas?.off('mouse:down', this.clickCallback);
        this.fabricCanvas?.off('mouse:up', this.clickCallback);
        this.fabricCanvas?.off('mouse:wheel', this.zoomCallback);
        super.dispose();
    }
    // #endregion
}

export { GFabricWorld };
